import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type {
  AnnouncementListFilters,
  AnnouncementStateDto,
} from '@/api/announcements-admin-client';
import { isAnnouncementState } from '@/lib/announcement-labels';
import {
  AnnouncementsScreen,
  type AnnouncementsTab,
} from '@/routes/admin/communication/announcements-screen';

// =============================================================================
// announcements-route.tsx — Adaptateur routeur ADMIN « Annonces » (R15-COM-03).
// =============================================================================
// L'ÉTAT DE VUE (onglet `tab`, annonce ouverte `announcement`, filtres du registre `state`/`campaignId`/
// `from`/`to`) est porté par l'URL via TanStack Router : partageable, compatible bouton retour, aucun état
// métier en localStorage (RULE_REF_006). Le composant de présentation (AnnouncementsScreen) reste testable
// hors routeur (props d'état). Défauts (tab=composer, aucun filtre) NON sérialisés → URL propre.

/** Forme validée du search de la route annonces admin (état d'URL). */
export interface AnnouncementsSearch {
  readonly tab?: AnnouncementsTab;
  readonly announcement?: string;
  readonly state?: AnnouncementStateDto;
  readonly campaignId?: string;
  readonly from?: string;
  readonly to?: string;
}

const TAB_VALUES: readonly AnnouncementsTab[] = ['composer', 'registre'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Valide/normalise le search (allowlist stricte, défauts non sérialisés → URL propre). */
export function validateAnnouncementsSearch(
  search: Record<string, unknown>,
): AnnouncementsSearch {
  const result: {
    tab?: AnnouncementsTab;
    announcement?: string;
    state?: AnnouncementStateDto;
    campaignId?: string;
    from?: string;
    to?: string;
  } = {};

  const tab = search['tab'];
  if (typeof tab === 'string' && (TAB_VALUES as readonly string[]).includes(tab)) {
    result.tab = tab as AnnouncementsTab;
  }
  const announcement = search['announcement'];
  if (typeof announcement === 'string' && announcement.length > 0) {
    result.announcement = announcement;
  }
  const state = search['state'];
  if (isAnnouncementState(state)) {
    result.state = state;
  }
  const campaignId = search['campaignId'];
  if (typeof campaignId === 'string' && campaignId.length > 0) {
    result.campaignId = campaignId;
  }
  const from = search['from'];
  if (typeof from === 'string' && ISO_DATE.test(from)) {
    result.from = from;
  }
  const to = search['to'];
  if (typeof to === 'string' && ISO_DATE.test(to)) {
    result.to = to;
  }
  return result;
}

/** Construit l'objet search sérialisable (clés vides omises → URL propre). */
function toSearch(
  tab: AnnouncementsTab,
  announcementId: string | undefined,
  filters: AnnouncementListFilters,
): AnnouncementsSearch {
  return {
    ...(tab !== 'composer' ? { tab } : {}),
    ...(announcementId !== undefined ? { announcement: announcementId } : {}),
    ...(filters.state !== undefined ? { state: filters.state } : {}),
    ...(filters.campaignId !== undefined ? { campaignId: filters.campaignId } : {}),
    ...(filters.from !== undefined ? { from: filters.from } : {}),
    ...(filters.to !== undefined ? { to: filters.to } : {}),
  };
}

export function AnnouncementsRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as AnnouncementsSearch;

  const activeTab: AnnouncementsTab = search.tab ?? 'composer';
  const selectedAnnouncementId = search.announcement;
  // Mémoïsé sur les champs de filtre : une nouvelle référence à chaque rendu déstabiliserait les
  // useCallback ci-dessous (react-hooks/exhaustive-deps).
  const filters: AnnouncementListFilters = React.useMemo(
    () => ({
      ...(search.state !== undefined ? { state: search.state } : {}),
      ...(search.campaignId !== undefined ? { campaignId: search.campaignId } : {}),
      ...(search.from !== undefined ? { from: search.from } : {}),
      ...(search.to !== undefined ? { to: search.to } : {}),
    }),
    [search.state, search.campaignId, search.from, search.to],
  );

  const onSelectTab = React.useCallback(
    (tab: AnnouncementsTab): void => {
      void navigate({
        to: '/admin/announcements',
        search: toSearch(tab, selectedAnnouncementId, filters),
      });
    },
    [navigate, selectedAnnouncementId, filters],
  );

  const onSelectAnnouncement = React.useCallback(
    (id: string | undefined): void => {
      void navigate({
        to: '/admin/announcements',
        search: toSearch(activeTab, id, filters),
      });
    },
    [navigate, activeTab, filters],
  );

  const onFiltersChange = React.useCallback(
    (next: AnnouncementListFilters): void => {
      void navigate({
        to: '/admin/announcements',
        search: toSearch(activeTab, selectedAnnouncementId, next),
      });
    },
    [navigate, activeTab, selectedAnnouncementId],
  );

  return (
    <AnnouncementsScreen
      activeTab={activeTab}
      selectedAnnouncementId={selectedAnnouncementId}
      filters={filters}
      onSelectTab={onSelectTab}
      onSelectAnnouncement={onSelectAnnouncement}
      onFiltersChange={onFiltersChange}
    />
  );
}
